import { Link } from "react-router";
import { Tooltip } from "@mui/material";
import type { GridRenderCellParams } from "@mui/x-data-grid-pro";
import cx from "classnames";
import TimeTransformer from "src/business-components/general/TimeTransformer/TimeTransformer";
import { MetricGroupTypeWithIcon } from "src/business-components/metrics/MetricGroupTypeWithIcon/MetricGroupTypeWithIcon";
import RunsStatusBars from "src/business-components/RunsStatusBars/RunsStatusBars.tsx";
import StatusesCount from "src/business-components/StatusesCount/StatusesCount";
import type { MetricGroupTypeName } from "src/business-utils/metrics/metricTypes";
import StatusBadgeItem from "src/components/features/small/StatusBadge/components/StatusBadgeItem/StatusBadgeItem";
import { useBuildLink } from "src/hooks/useBuildLink";
import type {
  PipelineDurationCol,
  PipelineGridRow,
  PipelineMetricCol,
} from "src/pages/PipelinesIndexPage/components/PipelinesTable/PipelinesTable.types.ts";
import { useSearchStorage } from "src/storage/useSearchStorage";
import type { AppPath, PitPath } from "src/utils/appRoutes/appRoutes.types";
import { formatLargeNumber } from "src/utils/number";
import WhiteTooltip from "@components/general/WhiteTooltip/WhiteTooltip";
import { TestsStatusIcon } from "@ui-icons";

type CellParams<T extends keyof PipelineGridRow> = GridRenderCellParams<
  PipelineGridRow,
  PipelineGridRow[T]
>;

export const metricHeader = (name: MetricGroupTypeName) => {
  const HeaderComponent = () => (
    <Tooltip arrow placement="top" title={name}>
      <MetricGroupTypeWithIcon
        type={name}
        iconSX={{
          color: (theme) => theme.vars?.palette?.neutral?.main,
          fontSize: "20px",
        }}
      />
    </Tooltip>
  );

  HeaderComponent.displayName = `MetricHeader(${name})`;

  return HeaderComponent;
};

interface PipelineTableTotalRunsProps extends CellParams<"runs"> {
  className?: string;
  totalRuns?: {
    count: number;
    statuses: any;
  };
  pipelineName: string;
}

export const PipelineTableTotalRuns = ({
  className,
  totalRuns,
  pipelineName,
}: PipelineTableTotalRunsProps) => {
  const TOOLTIP_DELAY = 300;
  const buildLink = useBuildLink();
  const { timeWindow } = useSearchStorage();

  const url = buildLink({
    app_name: pipelineName,
    search: { timeWindow: String(timeWindow) },
  } as AppPath);

  return (
    <WhiteTooltip
      arrow
      enterDelay={TOOLTIP_DELAY}
      enterNextDelay={TOOLTIP_DELAY}
      disableInteractive
      title={totalRuns?.statuses && <StatusesCount data={totalRuns.statuses} />}
    >
      <Link
        relative="path"
        to={url || "#"}
        className={cx("underline cursor-help", className)}
      >
        Total{" "}
        {formatLargeNumber(totalRuns?.count || 0, {
          notation: "standard",
        })}
      </Link>
    </WhiteTooltip>
  );
};

export const PipelinesTableRunsCell = (params: CellParams<"runs">) => {
  return (
    <div className="inline-flex flex-col">
      <RunsStatusBars
        slotProps={{
          RunStatusBar: {
            className: "w-[5.2px]! h-[24px]!",
          },
        }}
        data={params.value?.recentRuns || []}
        pipelineName={params.row.name}
      />
      <PipelineTableTotalRuns
        className="text-sm"
        totalRuns={params.value?.totalRuns}
        pipelineName={params.row.name}
      />
    </div>
  );
};

export const NACell = () => (
  <Tooltip arrow placement="top" title="N/A or no data">
    <div
      className="inline-block h-5 w-5 rounded-lg bg-diagonal-lines"
      style={
        { "--dark-c": "var(--mui-palette-neutral-dark)" } as React.CSSProperties
      }
    ></div>
  </Tooltip>
);

interface PipelineTableMetricCellProps {
  value?: PipelineMetricCol;
  pipelineName: string;
  extraHash?: string;
}

export const PipelineTableMetricCell = ({
  value,
  pipelineName,
  extraHash,
}: PipelineTableMetricCellProps) => {
  const buildLink = useBuildLink();
  const { timeWindow } = useSearchStorage();

  const searchParams: Record<string, string> = {
    timeWindow: String(timeWindow),
    tab: "metrics",
  };

  const hashValue = [
    value?.linkFilter,
    extraHash,
    value?.count ? `testsFilter="has-alerts"` : "",
  ]
    .filter(Boolean)
    .join("&");

  const url = buildLink({
    app_name: pipelineName,
    search: searchParams,
    hash: hashValue,
  } as AppPath);

  return value?.count === null ? (
    <NACell />
  ) : (
    <Link relative="path" to={url || "#"}>
      <StatusBadgeItem
        value={String(value?.count || "")}
        icon={
          <TestsStatusIcon
            status={value?.count ? "warning" : "success"}
            fontSize="0"
          />
        }
      />
    </Link>
  );
};

interface PipelineTableTimeCellProps {
  row: { name: string };
  value: string;
}

export const PipelineTableTimeCell = ({
  row,
  value,
}: PipelineTableTimeCellProps) => {
  const buildLink = useBuildLink();
  const { timeWindow } = useSearchStorage();

  const url = buildLink({
    app_name: row.name,
    app_pit: value,
    search: { timeWindow: String(timeWindow) },
  } as PitPath);

  return (
    <div className="inline-flex gap-2">
      <Link relative="path" to={url || "#"}>
        {/* <RunStatusBar status={row.recentRuns.at(-1)?.status} /> */}
        <TimeTransformer ts={value} multiline withoutTooltip ignoreTimezone />
      </Link>
    </div>
  );
};

interface PipelineTableDurationCellProps {
  value?: PipelineDurationCol;
  pipelineName: string;
}

export const PipelineTableDurationCell = ({
  value,
  pipelineName,
}: PipelineTableDurationCellProps) => {
  const buildLink = useBuildLink();
  const { timeWindow } = useSearchStorage();

  const testStatus =
    value?.has_incident == null ? null : value.has_incident ? "warning" : "success";

  // `asset_type` was migrated from URL hash → search params alongside the rest
  // of the asset-tree filter state (see `useAssetFilters`). `readable_name` is
  // still a hash-backed header filter atom.
  const url = buildLink({
    app_name: pipelineName,
    search: {
      timeWindow: String(timeWindow),
      tab: "metrics",
      asset_type: "pipeline",
    },
    hash: `readable_name="${value?.linkFilter}"`,
  } as AppPath);

  return value?.humanReadableValue ? (
    <Link relative="path" to={url || "#"}>
      <StatusBadgeItem
        tooltipTitle="Tests"
        value={`${value.humanReadableValue}`}
        icon={<TestsStatusIcon status={testStatus} fontSize="0" />}
      />
    </Link>
  ) : null;
};
